import { createElement } from "react";
import type { IconType } from "react-icons";
import {
  LuAntenna,
  LuBell,
  LuBird,
  LuBlocks,
  LuBookmark,
  LuBrain,
  LuBriefcase,
  LuCamera,
  LuClapperboard,
  LuClock3,
  LuCompass,
  LuCrosshair,
  LuCrown,
  LuFeather,
  LuFerrisWheel,
  LuFilm,
  LuFlame,
  LuGamepad2,
  LuGem,
  LuGhost,
  LuGlobe,
  LuHandCoins,
  LuHash,
  LuHeart,
  LuJoystick,
  LuLeaf,
  LuListTodo,
  LuMessageCircle,
  LuMusic4,
  LuPalette,
  LuPawPrint,
  LuRadar,
  LuRocket,
  LuShield,
  LuShoppingBag,
  LuSparkles,
  LuStar,
  LuTrophy,
  LuUsers,
  LuVenetianMask,
  LuWandSparkles,
  LuZap,
} from "react-icons/lu";

import { WHALE_ICON } from "@/utils/constants";

export type FeedSurfaceType = "builtin" | "list" | "custom-feed";

export type BuiltinFeedId =
  | "for-you"
  | "following"
  | "subscriptions"
  | "launchpad"
  | "trending"
  | "new"
  | "images"
  | "videos"
  | "audio"
  | "nfts";

export type FeedSurfaceRef =
  | { type: "builtin"; id: BuiltinFeedId }
  | { type: "list"; id: string }
  | { type: "custom-feed"; id: string };

const WhaleIcon: IconType = ({ className, size = "1em", style }) => {
  return createElement("img", {
    alt: "",
    "aria-hidden": true,
    className,
    src: WHALE_ICON,
    style: {
      height: size,
      objectFit: "contain",
      width: size,
      ...style,
    },
  });
};

export const FEED_SURFACE_ICON_REGISTRY = {
  bell: {
    icon: LuBell,
    label: "Bell",
  },
  antenna: {
    icon: LuAntenna,
    label: "Antenna",
  },
  bird: {
    icon: LuBird,
    label: "Bird",
  },
  bookmark: {
    icon: LuBookmark,
    label: "Bookmark",
  },
  blocks: {
    icon: LuBlocks,
    label: "Blocks",
  },
  brain: {
    icon: LuBrain,
    label: "Brain",
  },
  briefcase: {
    icon: LuBriefcase,
    label: "Briefcase",
  },
  camera: {
    icon: LuCamera,
    label: "Camera",
  },
  clapperboard: {
    icon: LuClapperboard,
    label: "Clapperboard",
  },
  clock: {
    icon: LuClock3,
    label: "Clock",
  },
  compass: {
    icon: LuCompass,
    label: "Compass",
  },
  crown: {
    icon: LuCrown,
    label: "Crown",
  },
  crosshair: {
    icon: LuCrosshair,
    label: "Crosshair",
  },
  feather: {
    icon: LuFeather,
    label: "Feather",
  },
  "ferris-wheel": {
    icon: LuFerrisWheel,
    label: "Ferris Wheel",
  },
  film: {
    icon: LuFilm,
    label: "Film",
  },
  flame: {
    icon: LuFlame,
    label: "Flame",
  },
  gamepad: {
    icon: LuGamepad2,
    label: "Gamepad",
  },
  gem: {
    icon: LuGem,
    label: "Gem",
  },
  ghost: {
    icon: LuGhost,
    label: "Ghost",
  },
  globe: {
    icon: LuGlobe,
    label: "Globe",
  },
  "hand-coins": {
    icon: LuHandCoins,
    label: "Coins",
  },
  hash: {
    icon: LuHash,
    label: "Hash",
  },
  heart: {
    icon: LuHeart,
    label: "Heart",
  },
  joystick: {
    icon: LuJoystick,
    label: "Joystick",
  },
  leaf: {
    icon: LuLeaf,
    label: "Leaf",
  },
  list: {
    icon: LuListTodo,
    label: "List",
  },
  "message-circle": {
    icon: LuMessageCircle,
    label: "Messages",
  },
  music: {
    icon: LuMusic4,
    label: "Music",
  },
  palette: {
    icon: LuPalette,
    label: "Palette",
  },
  "paw-print": {
    icon: LuPawPrint,
    label: "Paw Print",
  },
  radar: {
    icon: LuRadar,
    label: "Radar",
  },
  rocket: {
    icon: LuRocket,
    label: "Rocket",
  },
  shield: {
    icon: LuShield,
    label: "Shield",
  },
  "shopping-bag": {
    icon: LuShoppingBag,
    label: "Shopping Bag",
  },
  sparkles: {
    icon: LuSparkles,
    label: "Sparkles",
  },
  star: {
    icon: LuStar,
    label: "Star",
  },
  trophy: {
    icon: LuTrophy,
    label: "Trophy",
  },
  users: {
    icon: LuUsers,
    label: "Users",
  },
  mask: {
    icon: LuVenetianMask,
    label: "Mask",
  },
  wand: {
    icon: LuWandSparkles,
    label: "Wand",
  },
  whale: {
    icon: WhaleIcon,
    label: "Whale",
  },
  zap: {
    icon: LuZap,
    label: "Zap",
  },
} as const satisfies Record<string, { icon: IconType; label: string }>;

export type FeedSurfaceIconKey = keyof typeof FEED_SURFACE_ICON_REGISTRY;

export const FEED_MANAGER_ICON_PICKER_KEYS = [
  "bell",
  "bookmark",
  "clock",
  "crown",
  "flame",
  "hash",
  "heart",
  "list",
  "rocket",
  "star",
  "zap",
  "antenna",
  "bird",
  "blocks",
  "brain",
  "camera",
  "clapperboard",
  "compass",
  "crosshair",
  "feather",
  "ferris-wheel",
  "film",
  "gamepad",
  "ghost",
  "hand-coins",
  "joystick",
  "leaf",
  "music",
  "palette",
  "paw-print",
  "radar",
  "shield",
  "shopping-bag",
  "trophy",
  "wand",
] as const satisfies readonly FeedSurfaceIconKey[];

export const DEFAULT_FEED_SURFACE_ICON_KEY: FeedSurfaceIconKey = "list";

export type BuiltinFeedDefinition = {
  id: BuiltinFeedId;
  label: string;
  description: string;
  iconKey: FeedSurfaceIconKey;
  loggedInOnly: boolean;
  defaultVisible: boolean;
  canMove: boolean;
  canRemoveFromDisplay: boolean;
};

export const BUILTIN_FEED_REGISTRY = {
  "for-you": {
    id: "for-you",
    label: "For You",
    description: "Personalized picks from your default home feed.",
    iconKey: "globe",
    loggedInOnly: false,
    defaultVisible: true,
    canMove: false,
    canRemoveFromDisplay: false,
  },
  following: {
    id: "following",
    label: "Following",
    description: "Posts from accounts you follow.",
    iconKey: "users",
    loggedInOnly: true,
    defaultVisible: true,
    canMove: false,
    canRemoveFromDisplay: false,
  },
  subscriptions: {
    id: "subscriptions",
    label: "Subscriptions",
    description: "Subscriber-only posts from creators you support.",
    iconKey: "crown",
    loggedInOnly: true,
    defaultVisible: true,
    canMove: false,
    canRemoveFromDisplay: false,
  },
  launchpad: {
    id: "launchpad",
    label: "Launchpad",
    description: "Upcoming launches and launchpad activity.",
    iconKey: "gem",
    loggedInOnly: false,
    defaultVisible: true,
    canMove: true,
    canRemoveFromDisplay: true,
  },
  trending: {
    id: "trending",
    label: "Trending",
    description: "Popular posts from the last 7 days.",
    iconKey: "flame",
    loggedInOnly: true,
    defaultVisible: false,
    canMove: true,
    canRemoveFromDisplay: true,
  },
  new: {
    id: "new",
    label: "New",
    description: "Most recent posts across Focus.",
    iconKey: "sparkles",
    loggedInOnly: true,
    defaultVisible: false,
    canMove: true,
    canRemoveFromDisplay: true,
  },
  images: {
    id: "images",
    label: "Images",
    description: "Posts with public or gated images.",
    iconKey: "camera",
    loggedInOnly: true,
    defaultVisible: false,
    canMove: true,
    canRemoveFromDisplay: true,
  },
  videos: {
    id: "videos",
    label: "Videos",
    description: "Posts with public or gated videos.",
    iconKey: "clapperboard",
    loggedInOnly: true,
    defaultVisible: false,
    canMove: true,
    canRemoveFromDisplay: true,
  },
  audio: {
    id: "audio",
    label: "Audio",
    description: "Posts with public or gated audio.",
    iconKey: "music",
    loggedInOnly: true,
    defaultVisible: false,
    canMove: true,
    canRemoveFromDisplay: true,
  },
  nfts: {
    id: "nfts",
    label: "NFTs",
    description: "Posts that were minted as NFTs.",
    iconKey: "crown",
    loggedInOnly: true,
    defaultVisible: false,
    canMove: true,
    canRemoveFromDisplay: true,
  },
} as const satisfies Record<BuiltinFeedId, BuiltinFeedDefinition>;

export const BUILTIN_FEED_ORDER: BuiltinFeedId[] = [
  "for-you",
  "following",
  "subscriptions",
  "launchpad",
  "trending",
  "new",
  "images",
  "videos",
  "audio",
  "nfts",
];

export const isBuiltinFeedId = (value: unknown): value is BuiltinFeedId => {
  return typeof value === "string" && value in BUILTIN_FEED_REGISTRY;
};

export const isFeedSurfaceIconKey = (
  value: unknown,
): value is FeedSurfaceIconKey => {
  return typeof value === "string" && value in FEED_SURFACE_ICON_REGISTRY;
};

export const getFeedSurfaceIconKey = (
  value?: string | null,
): FeedSurfaceIconKey => {
  return isFeedSurfaceIconKey(value) ? value : DEFAULT_FEED_SURFACE_ICON_KEY;
};

export const getFeedSurfaceIcon = (value?: string | null): IconType => {
  return FEED_SURFACE_ICON_REGISTRY[getFeedSurfaceIconKey(value)].icon;
};

export const getBuiltinFeedDefinition = (id: BuiltinFeedId) => {
  return BUILTIN_FEED_REGISTRY[id];
};

export const getAvailableBuiltinFeedDefinitions = (loggedIn: boolean) => {
  return BUILTIN_FEED_ORDER.map((id) => BUILTIN_FEED_REGISTRY[id]).filter(
    (builtin) => loggedIn || !builtin.loggedInOnly,
  );
};

export const getRequiredBuiltinFeedDefinitions = (loggedIn: boolean) => {
  return getAvailableBuiltinFeedDefinitions(loggedIn).filter(
    (builtin) => !builtin.canRemoveFromDisplay,
  );
};

export const createBuiltinFeedRef = (id: BuiltinFeedId): FeedSurfaceRef => ({
  type: "builtin",
  id,
});

export const isFeedSurfaceRef = (value: unknown): value is FeedSurfaceRef => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }

  const candidate = value as { type?: unknown; id?: unknown };

  if (candidate.type === "builtin") {
    return isBuiltinFeedId(candidate.id);
  }

  return (
    (candidate.type === "list" || candidate.type === "custom-feed") &&
    typeof candidate.id === "string" &&
    candidate.id.length > 0
  );
};

export const getFeedSurfaceKey = (surface: FeedSurfaceRef) => {
  return `${surface.type}:${surface.id}`;
};

export const areFeedSurfaceRefsEqual = (
  left?: FeedSurfaceRef | null,
  right?: FeedSurfaceRef | null,
) => {
  if (!left || !right) {
    return false;
  }

  return left.type === right.type && left.id === right.id;
};

export const serializeFeedSurfaceRef = (surface: FeedSurfaceRef) => {
  return JSON.stringify(surface);
};

export const parseFeedSurfaceRef = (value: unknown): FeedSurfaceRef | null => {
  if (isFeedSurfaceRef(value)) {
    return value;
  }

  if (typeof value !== "string" || value.length === 0) {
    return null;
  }

  try {
    const parsed = JSON.parse(value);
    return isFeedSurfaceRef(parsed) ? parsed : null;
  } catch {
    return null;
  }
};
